import { Component, computed, effect, inject, signal, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatChipsModule } from '@angular/material/chips';
import { MatButtonModule } from '@angular/material/button';
import { MatToolbarModule } from '@angular/material/toolbar';
import { AssociationWithName, TaskQueueInterface, TaskQueueStorageServiceInterface } from './interfaces';
import {
  Activity,
  ActivityAssociation,
  CRMActivityApiService,
  CrmObjectInterface,
  UpdateActivityRequestInterface,
} from '@vendasta/crm';
import { combineLatest, firstValueFrom, map, Observable, of } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { ActivityTaskStatus, CrmFieldService, PAGE_ROUTES, StandardExternalIds, StandardIds } from '@galaxy/crm/static';
import { CrmRescheduleTaskComponent, RescheduleEvent } from './reschedule-task/reschedule-task.component';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { TranslateModule } from '@ngx-translate/core';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { format, ParsedNumber, parseNumber } from 'libphonenumber-js';
import { startWith } from 'rxjs/operators';
import { ProductAnalyticsService } from '@vendasta/product-analytics';
import { CrmInjectionToken, CrmObjectService, CrmMultilocationInjectionToken } from '@galaxy/crm/static';
import { TaskQueueService } from './task-queue.service';

@Component({
  selector: 'crm-task-queue',
  templateUrl: 'task-queue.component.html',
  styleUrls: ['task-queue.component.scss'],
  imports: [
    CommonModule,
    MatButtonModule,
    MatChipsModule,
    MatIconModule,
    MatToolbarModule,
    CrmRescheduleTaskComponent,
    TranslateModule,
    MatProgressSpinnerModule,
    MatMenuModule,
  ],
})
export class CrmTaskQueueComponent {
  private readonly crmObjectService = inject(CrmObjectService);
  private readonly storageService = inject(TaskQueueStorageServiceInterface);
  private readonly config = inject(CrmInjectionToken);
  private readonly activityApiService = inject(CRMActivityApiService);
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly fieldService = inject(CrmFieldService);
  private readonly snackbarService = inject(SnackbarService);
  private readonly analyticsService = inject(ProductAnalyticsService);
  private readonly taskQueueService = inject(TaskQueueService);
  private readonly multiLocationConfig = inject(CrmMultilocationInjectionToken);

  protected readonly taskQueue = this.taskQueueService.taskQueue;
  protected readonly hasTasks = computed(() => this.queueHasTasks(this.taskQueue()));
  protected readonly hasPendingTasks = computed(() => this.queueHasPendingTasks(this.taskQueue()));
  protected readonly currentTaskId = computed(() => this.taskQueueService.queueCurrentID(this.taskQueue()));
  protected readonly currentTask = signal<Activity | null>(null);
  protected readonly currentTaskName = computed(() => this.getTaskName(this.currentTask()));
  protected readonly currentAssociations = signal<AssociationWithName[]>([]);
  protected readonly loading = this.taskQueueService.loading;
  protected readonly action = this.taskQueueService.action;
  protected readonly isResolved = computed(() => {
    const isCompleted = this.currentTaskStatus() === ActivityTaskStatus.Completed;
    const actionDone = Boolean(this.action()?.type);
    return isCompleted || actionDone;
  });

  @ViewChild('rescheduleTask') rescheduleTask?: CrmRescheduleTaskComponent;
  protected readonly currentTaskStatus = computed(() => this.getTaskStatus(this.currentTask()));

  protected readonly updateTaskLoadingStatus = signal<boolean>(false);

  protected currentPageId$: Observable<string>;

  constructor() {
    this.storageService.load().then((taskQueue) => this.taskQueue.set(taskQueue));

    effect(() => {
      //fetch associations
      const task = this.currentTask();
      this.fetchTaskAssociations(task).then((associations) => this.currentAssociations.set(associations));
      // fetch the activity and update currentTask signal when the currentTaskId changes
      const newId = this.currentTaskId();
      const currentId = task?.crmObjectId;
      if (newId !== currentId) {
        this.taskQueueService.fetchCurrentTask(newId).then((task) => this.currentTask.set(task));
      }
    });

    //watch for when we navigate to a company or contact page
    this.currentPageId$ = this.activatedRoute.params.pipe(
      map((routeParams) => routeParams['crmObjectId'] || 'none'),
      startWith('none'),
    );
  }

  async startQueue(ids: string[], namespace?: string): Promise<void> {
    this.taskQueueService.startQueue(ids, namespace);
  }

  protected async handleReschedule(event: RescheduleEvent): Promise<void> {
    try {
      const task = this.currentTask();
      if (task && event.changed) {
        const namespace = await firstValueFrom(this.config.namespace$);
        const request: UpdateActivityRequestInterface = {
          namespace,
          activity: {
            crmObjectId: task.crmObjectId,
            fields: [
              {
                fieldId: StandardIds.ActivityTaskDueDate,
                dateValue: event.dueDate,
              },
            ],
          },
          fieldMask: { paths: [StandardIds.ActivityTaskDueDate] },
        };
        await this.updateTask(request);
      }
      this.rescheduleTask?.closePopover();
      this.snackbarService.openSuccessSnack('TASK_QUEUE.SUCCESS_MESSAGES.RESCHEDULE');
      if (this.hasPendingTasks()) {
        // move to next task
        this.handleNextClick();
      } else {
        // show the resolved state so the user can go back to the task list
        this.action.set({ type: 'reschedule' });
      }
    } catch {
      this.snackbarService.openErrorSnack('TASK_QUEUE.ERRORS.RESCHEDULE');
    }
    this.analyticsService.trackEvent('task-queue-reschedule-task', 'sales-tasks', 'click', 1);
  }

  protected async handleSkipClick(): Promise<void> {
    const queue = this.taskQueue();
    if (queue) {
      const current = queue.tasks[queue.current];
      queue.tasks.splice(queue.current, 1);
      queue.tasks.push(current);
      await this.taskQueueService.saveQueue(queue);
      this.taskQueueService.navigateToCurrentTask(queue);
    }
    this.analyticsService.trackEvent('task-queue-skip-task', 'sales-tasks', 'click', 1);
  }

  protected async handleNextClick(): Promise<void> {
    const queue = this.taskQueue();
    if (queue) {
      queue.current++;
      await this.taskQueueService.saveQueue(queue);
      this.taskQueueService.navigateToCurrentTask(queue);
    }
    this.analyticsService.trackEvent('task-queue-next-task', 'sales-tasks', 'click', 1);
  }

  protected handleEmptyClick(): void {
    this.taskQueue.set(null);
    this.storageService.empty();
    this.analyticsService.trackEvent('task-queue-exit-queue', 'sales-tasks', 'click', 1);
  }

  protected async handleCompleteClick(): Promise<void> {
    try {
      this.updateTaskLoadingStatus.set(true);
      const task = this.currentTask();

      if (!task) {
        return;
      }
      let namespace = task.namespace;
      if (task.crmObjectId && this.taskQueueService.hasTaskNamespaces()) {
        const storedNamespace = this.taskQueueService.getTaskNamespace(task.crmObjectId);
        if (storedNamespace) {
          namespace = storedNamespace;
        }
      }
      if (!namespace) {
        namespace = await firstValueFrom(this.config.namespace$);
      }

      const request: UpdateActivityRequestInterface = {
        namespace,
        activity: {
          crmObjectId: task.crmObjectId,
          fields: [
            {
              fieldId: StandardIds.ActivityTaskStatus,
              stringValue: ActivityTaskStatus.Completed,
            },
          ],
        },
        fieldMask: { paths: [StandardIds.ActivityTaskStatus] },
      };

      const undoRequest: UpdateActivityRequestInterface = {
        namespace,
        activity: {
          crmObjectId: task.crmObjectId,
          fields: [
            {
              fieldId: StandardIds.ActivityTaskStatus,
              stringValue: task.fields?.find((f) => f.fieldId === StandardIds.ActivityTaskStatus)?.stringValue || '',
            },
          ],
        },
        fieldMask: { paths: [StandardIds.ActivityTaskStatus] },
      };

      await this.updateTask(request);

      const queue = this.taskQueue();
      this.action.set({ type: 'complete', undoRequest });
      this.snackbarService.openSuccessSnack('TASK_QUEUE.ACTIONS.MARK_AS_COMPLETE');
      if (queue) {
        this.taskQueue.set({ ...queue });
      }
    } catch (error) {
      this.snackbarService.openErrorSnack('TASK_QUEUE.ERRORS.COMPLETE');
    } finally {
      this.updateTaskLoadingStatus.set(false);
    }
    this.analyticsService.trackEvent('task-queue-complete-task', 'sales-tasks', 'click', 1);
  }

  protected async handleUndoClick(): Promise<void> {
    const req = this.action()?.undoRequest;
    if (req) {
      try {
        await this.updateTask(req);
        this.action.set(null);
      } catch {
        this.snackbarService.openErrorSnack('TASK_QUEUE.ERRORS.UNDO');
      }
    }
    this.analyticsService.trackEvent('task-queue-undo-task', 'sales-tasks', 'click', 1);
  }

  protected async handleBackToTasksClick(): Promise<void> {
    this.taskQueue.set(null);
    await this.storageService.empty();
    const routePrefix = await firstValueFrom(this.config.routePrefix$);
    this.router.navigateByUrl(`${routePrefix}/${PAGE_ROUTES.TASK.ROOT}/${PAGE_ROUTES.TASK.SUBROUTES.LIST}`);
    this.analyticsService.trackEvent('task-queue-back-to-table', 'sales-tasks', 'click', 1);
  }

  private async updateTask(req: UpdateActivityRequestInterface): Promise<void> {
    try {
      this.updateTaskLoadingStatus.set(true);
      await firstValueFrom(this.activityApiService.updateActivity(req));
      const taskId = this.currentTaskId();
      if (taskId) {
        if (req.namespace) {
          this.taskQueueService.setCurrentTaskNamespace(req.namespace);
        }
        const updatedTask = await this.taskQueueService.fetchCurrentTask(taskId);
        this.currentTask.set(updatedTask);
      }
    } finally {
      this.updateTaskLoadingStatus.set(false);
    }
    this.analyticsService.trackEvent('task-queue-update-task', 'sales-tasks', 'click', 1);
  }

  private queueHasTasks(queue: TaskQueueInterface | null): boolean {
    return Boolean(queue?.tasks?.length);
  }

  private queueHasPendingTasks(queue: TaskQueueInterface | null): boolean {
    const current = queue?.current || 0;
    const length = queue?.tasks.length || 0;
    return current < length - 1;
  }

  getDisplayName(crmObject: CrmObjectInterface): string {
    const companyName = this.fieldService.getFieldValueFromCrmObject(crmObject, StandardIds.CompanyName);
    if (companyName?.stringValue) {
      return companyName.stringValue;
    }

    const firstName = this.fieldService.getFieldValueFromCrmObject(crmObject, StandardExternalIds.FirstName);
    const lastName = this.fieldService.getFieldValueFromCrmObject(crmObject, StandardExternalIds.LastName);
    if (firstName?.stringValue || lastName?.stringValue) {
      return `${firstName?.stringValue ?? ''} ${lastName?.stringValue ?? ''}`.trim();
    }

    const email = this.fieldService.getFieldValueFromCrmObject(crmObject, StandardExternalIds.Email);
    if (email?.stringValue) {
      return email.stringValue;
    }

    const phone = this.fieldService.getFieldValueFromCrmObject(crmObject, StandardExternalIds.PhoneNumber);
    if (phone?.stringValue) {
      return this.formatPhoneNumber(phone.stringValue);
    }

    return crmObject?.crmObjectId || '';
  }

  private formatPhoneNumber(phoneNumber: string): string {
    const parsedNumber = parseNumber(phoneNumber, {
      defaultCountry: 'US',
    });
    if (Object.keys(parsedNumber).length === 0) {
      return '';
    }

    return format(parsedNumber as ParsedNumber, 'INTERNATIONAL');
  }

  private mapAssociationNamesToIds(associations: CrmObjectInterface[]): Map<string, string> {
    const idToName = new Map<string, string>();
    associations?.forEach((association) => {
      if (association?.crmObjectId) idToName.set(association.crmObjectId, this.getDisplayName(association));
    });
    return idToName;
  }

  private async fetchTaskAssociations(task: Activity | null): Promise<AssociationWithName[]> {
    if (!task?.associations) return [];
    const contacts: string[] = [];
    const companies: string[] = [];
    const associations: CrmObjectInterface[] = [];
    task.associations.forEach((association) => {
      if (association.crmObjectType === 'Contact') {
        contacts.push(association.crmObjectId);
        associations.push(association);
      } else if (association.crmObjectType === 'Company') {
        companies.push(association.crmObjectId);
        associations.push(association);
      }
    });

    const contacts$ =
      contacts.length > 0
        ? this.crmObjectService.getMultiObject('Contact', contacts).pipe(map((contact) => contact.crmObjects))
        : of([]);
    const companies$ =
      companies.length > 0
        ? this.crmObjectService.getMultiObject('Company', companies).pipe(map((company) => company.crmObjects))
        : of([]);

    const displayNames$ = combineLatest([contacts$, companies$]).pipe(
      map(([contacts, companies]) => {
        const associations = (contacts || []).concat(companies || []);
        return this.mapAssociationNamesToIds(associations);
      }),
    );

    try {
      const displayNames = await firstValueFrom(displayNames$);
      return (
        associations
          .filter((association) => displayNames.has(association.crmObjectId || ''))
          .map((association) => {
            return {
              name: displayNames.get(association.crmObjectId || ''),
              association: association,
            } as AssociationWithName;
          })
          .sort((a, b) => {
            const nameA = a.name.toLowerCase();
            const nameB = b.name.toLowerCase();
            if (nameA > nameB) {
              return 1;
            }
            if (nameA < nameB) {
              return -1;
            }
            return 0;
          }) || []
      );
    } catch {
      return [];
    }
  }

  private getTaskName(task: Activity | null): string {
    if (task) {
      return this.fieldService.getFieldValueFromCrmObject(task, StandardIds.ActivityName)?.stringValue ?? '';
    }
    return '';
  }

  protected async changeAssociation(association?: ActivityAssociation): Promise<void> {
    this.analyticsService.trackEvent('task-queue-change-association', 'sales-tasks', 'click', 1);
    return this.taskQueueService.navigateToAssociation(association);
  }

  private getTaskStatus(task: Activity | null): string {
    if (task) {
      return this.fieldService.getFieldValueFromCrmObject(task, StandardIds.ActivityTaskStatus)?.stringValue ?? '';
    }
    return '';
  }
}
